import React from "react";

import ScaleIcon from "@uui/Scales01";
import { isEmpty } from "lodash/fp";

import { Skeleton } from "@mui/material";

import {
  CoachingModule,
  CoachingModuleContent,
  CoachingModuleEmptySummary,
  CoachingModuleHeader,
  CoachingModuleSummary,
  CoachingModuleTitle,
} from "@app/analytics/components/CoachingModule";
import ErrorState from "@app/analytics/components/ErrorState";
import { MetricGradientRange } from "@app/analytics/components/GradientRange/CoachingGradientRange";
import Table from "@app/analytics/components/Table";
import TableBody from "@app/analytics/components/Table/TableBody";
import TableCell from "@app/analytics/components/Table/TableCell";
import TableHead from "@app/analytics/components/Table/TableHead";
import TableRow from "@app/analytics/components/Table/TableRow";
import Tip from "@app/analytics/components/Tip";
import BenchmarkOverlay from "@app/analytics/MeetingReport/components/BenchmarkOverlay";
import ScoreProgress from "@app/analytics/MeetingReport/components/ScoreProgress";
import { LoadingSkeleton } from "@app/analytics/MeetingReport/styles";
import { TOOLTIPS } from "@app/common/content";
import useDataTable, { HeadCell } from "@app/common/hooks/useDataTable";
import { getSemanticScore, SemanticScore } from "@app/common/utils";

import { Column } from "@rui/Stack";

import useCharismaAndBias, {
  CharismaBiasParticipant,
} from "./hooks/useCharismaAndBias";
import InsufficientData from "./InsufficientData";
import ParticipantsNotShownCell from "./ParticipantsNotShownCell";
import {
  Description,
  ModuleTableContainer,
  TableCellWithOverflow,
} from "./styles";

const MAX_TABLE_ROWS = 6;

const HEAD_CELLS: HeadCell<CharismaBiasParticipant>[] = [
  {
    id: "name",
    label: "Participant Name",
    sortable: false,
  },
  {
    id: "talkTime",
    label: "Talk Time",
    sortable: false,
  },
  {
    id: "bias",
    label: "Your Bias",
    sortable: false,
  },
];

const DESCRIPTION_BY_SCORE = {
  negative:
    "Your bias is below recommended, which means you could try to respond more positively when others speak.",
  neutral:
    "Your bias is slightly below recommended, which means you could try to respond more positively when others speak.",
  positive:
    "Nice! Your bias is within the recommended range, which means you respond well when others speak.",
  unknown: "",
};

const BiasSummary = ({
  bias,
  isLoading,
  semanticScore,
}: {
  bias: number | null;
  isLoading: boolean;
  semanticScore: SemanticScore;
}) => {
  if (!isLoading && !bias) {
    return (
      <CoachingModuleEmptySummary>
        Not enough visual data
      </CoachingModuleEmptySummary>
    );
  }

  return (
    <BenchmarkOverlay
      renderControl={({ onClick }) => (
        <CoachingModuleSummary icon={semanticScore} onClick={onClick}>
          {isLoading ? <Skeleton width={50} /> : bias ?? "-"}
        </CoachingModuleSummary>
      )}
      title="Range"
    >
      <MetricGradientRange value={bias} withMarkerLabel />
    </BenchmarkOverlay>
  );
};

const BiasModule = () => {
  const {
    data: { my, participants },
    isError,
    isInitialLoading,
    refetch,
  } = useCharismaAndBias();

  const biasParticipants = participants.filter((p) => p.bias != null);

  const { order, orderBy, handleSort, tableRows } = useDataTable(
    biasParticipants,
    HEAD_CELLS,
    { defaultOrderBy: "bias" }
  );

  const includeNotShownRow = tableRows.length > MAX_TABLE_ROWS;
  const rowsToShow = includeNotShownRow
    ? [
        ...tableRows.slice(0, MAX_TABLE_ROWS / 2),
        ...tableRows.slice(tableRows.length - MAX_TABLE_ROWS / 2),
      ]
    : tableRows;

  const showTable = !isEmpty(rowsToShow);
  const semanticScore = getSemanticScore(my.bias);

  const showEmptyState = !isInitialLoading && !my.bias;

  return (
    <CoachingModule expandable>
      <CoachingModuleHeader>
        <CoachingModuleTitle Icon={ScaleIcon} tooltip={TOOLTIPS.coaching.bias}>
          Bias
        </CoachingModuleTitle>
        <BiasSummary
          bias={my.bias}
          semanticScore={semanticScore}
          isLoading={isInitialLoading}
        />
      </CoachingModuleHeader>
      <CoachingModuleContent>
        {isError ? (
          <ErrorState refetch={refetch} sx={{ minHeight: "150px" }} />
        ) : showEmptyState ? (
          <InsufficientData type="camera">
            Your time on camera was too low to generate bias.
          </InsufficientData>
        ) : (
          <Column spacing={1.5}>
            {isInitialLoading ? (
              <LoadingSkeleton height={50} />
            ) : (
              <>
                <Description>{DESCRIPTION_BY_SCORE[semanticScore]}</Description>
                {showTable && (
                  <ModuleTableContainer>
                    <Table>
                      <TableHead
                        headCells={HEAD_CELLS}
                        onSort={handleSort}
                        order={order}
                        orderBy={orderBy}
                      />
                      <TableBody>
                        {rowsToShow.map((row, i) => (
                          <React.Fragment key={row.id}>
                            {i === MAX_TABLE_ROWS / 2 && includeNotShownRow && (
                              <ParticipantsNotShownCell
                                numHiddenParticipants={
                                  tableRows.length - MAX_TABLE_ROWS
                                }
                              />
                            )}
                            <TableRow>
                              <TableCellWithOverflow>
                                {row.name}
                              </TableCellWithOverflow>
                              <TableCell>
                                {row.talkTimePercent} ({row.talkTime})
                              </TableCell>
                              <TableCell>
                                <ScoreProgress
                                  value={row.bias}
                                  sx={{ maxWidth: "200px" }}
                                />
                              </TableCell>
                            </TableRow>
                          </React.Fragment>
                        ))}
                      </TableBody>
                    </Table>
                  </ModuleTableContainer>
                )}
              </>
            )}
            <Tip>
              To avoid perceived biases, try to stay engaged for all speakers.
              Positive body language like eye contact and head nodding helps to
              show the speaker you’re paying attention.
            </Tip>
          </Column>
        )}
      </CoachingModuleContent>
    </CoachingModule>
  );
};

export default BiasModule;
